/**
 * Device-scoped camera panel (CAM-S4 device UX + CAM-S2b ONVIF discovery).
 * Mounted on #/devices/:id — not Account.
 */

import { apiBase, fetchWithDeadline } from "./api.js";
import {
  CAMERAS_CONSOLE_TIMEOUT_MS,
  CAMERAS_SNAPSHOT_IMAGE_TIMEOUT_MS,
  cameraApiPathWithOwner,
  buildGetAdminCamerasPath,
  buildPostAdminCamerasPath,
  buildPostAdminCamerasTestCapturePath,
  buildGetAdminDevicesCameraBindingsPath,
  buildPostAdminDevicesCameraBindingsPath,
  buildDeleteAdminDevicesCameraBindingsPath,
  buildGetAdminDevicesCameraDiscoveryPath,
  buildPostAdminDevicesCameraDiscoveryPath,
  buildGetAdminCameraSnapshotImagePath,
} from "./cameras-console-api.js";

/** Bearer header for snapshot GETs (``getToken`` from shell globals). */
function snapshotAuthHeaders() {
  const headers = {};
  const tokenFn = globalThis.getToken;
  if (typeof tokenFn === "function") {
    const token = tokenFn();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** GET snapshot JPEG; returns ``{ ok, status?, blob? }`` (CAM-GW-F1d). */
async function fetchSnapshotImage(imgPath, timeoutMs = CAMERAS_SNAPSHOT_IMAGE_TIMEOUT_MS) {
  const fullUrl = `${apiBase()}${imgPath}`;
  const url = `${fullUrl}${fullUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
  const r = await fetchWithDeadline(
    url,
    { method: "GET", credentials: "include", headers: snapshotAuthHeaders() },
    timeoutMs,
  );
  if (!r.ok) return { ok: false, status: r.status };
  const blob = await r.blob();
  if (!blob || blob.size === 0) return { ok: false, status: r.status };
  return { ok: true, blob };
}

/** Render blob into preview container. */
function showSnapshotBlob(preview, blob) {
  if (!preview || !blob) return;
  const objUrl = URL.createObjectURL(blob);
  const img = document.createElement("img");
  img.alt = "snapshot preview";
  img.style.maxWidth = "100%";
  img.style.maxHeight = "240px";
  img.style.borderRadius = "8px";
  img.onload = () => URL.revokeObjectURL(objUrl);
  img.onerror = () => URL.revokeObjectURL(objUrl);
  img.src = objUrl;
  preview.innerHTML = "";
  preview.appendChild(img);
}

/**
 * @param {{ canEdit: boolean }} opts
 */
export function deviceCamerasPanelHtml(opts) {
  if (!opts.canEdit) {
    return `<details class="card device-drawer" id="devCamerasCard">
      <summary class="device-drawer__summary">
        <span class="device-drawer__title">Cameras</span>
        <span class="device-drawer__hint muted">Account owner only</span>
      </summary>
      <div class="device-drawer__body">
        <p class="muted" style="margin:0">Camera setup is available on boards you own. Shared boards use the owner&rsquo;s camera bindings.</p>
      </div>
    </details>`;
  }
  return `<details class="card device-drawer" id="devCamerasCard">
    <summary class="device-drawer__summary">
      <span class="device-drawer__title">Cameras</span>
      <span class="device-drawer__hint muted">Find cameras · bind · test</span>
    </summary>
    <div class="device-drawer__body">
      <p class="muted" style="margin:0 0 10px">Search finds LAN cameras via this board. Pick a host, enter credentials, test, then bind for alarm snapshots.</p>
      <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:10px">
        <button type="button" class="btn secondary btn-tap" id="devCamSearch">Search cameras</button>
        <span class="muted" id="devCamSearchStatus" style="font-size:12px;min-height:1.2em"></span>
      </div>
      <div id="devCamDiscoverList" class="muted" style="margin-bottom:12px;font-size:13px">Click Search to find cameras on the board network.</div>
      <form id="devCamAddForm" class="inline-form" style="margin-top:8px">
        <label class="field"><span>Label</span><input id="devCamLabel" placeholder="Gate cam" /></label>
        <label class="field"><span>Host</span><input id="devCamHost" placeholder="192.168.1.64" required /></label>
        <label class="field"><span>Protocol</span>
          <select id="devCamProto">
            <option value="onvif">Network camera</option>
            <option value="http_snapshot">HTTP snapshot</option>
            <option value="hikvision">Hikvision ISAPI</option>
            <option value="dahua">Dahua</option>
          </select>
        </label>
        <label class="field"><span>Username</span><input id="devCamUser" autocomplete="off" /></label>
        <label class="field"><span>Password</span><input id="devCamPass" type="password" autocomplete="new-password" /></label>
        <div class="row wide" style="justify-content:flex-end;gap:8px">
          <button class="btn secondary" type="button" id="devCamTestNew">Test connection</button>
          <button class="btn" type="submit">Save camera</button>
        </div>
        <p id="devCamTestMsg" class="muted" style="margin-top:8px;font-size:12px"></p>
        <div id="devCamTestPreview" style="margin-top:8px"></div>
      </form>
      <div id="devCamBindings" class="muted" style="margin-top:14px">Loading bindings…</div>
    </div>
  </details>`;
}

/**
 * @param {HTMLElement} view
 * @param {{ deviceId: string, ownerAdmin?: string, routeSeq: number, canEdit: boolean, isRouteCurrent: (n: number) => boolean, timeoutMs?: number }} ctx
 */
export function wireDeviceCamerasPanel(view, ctx) {
  const {
    deviceId,
    ownerAdmin = "",
    routeSeq,
    canEdit,
    isRouteCurrent,
    api,
    toast,
    escapeHtml,
    timeoutMs: timeoutMsIn,
  } = ctx;
  const camPath = (p) => cameraApiPathWithOwner(p, ownerAdmin);
  const timeoutMs = timeoutMsIn || CAMERAS_CONSOLE_TIMEOUT_MS;

  /** Poll snapshot image until blob ready or deadline (404 = still queued). */
  const pollSnapshotPreview = async (imgPath, preview, msg, deadlineMs = 45000) => {
    const started = Date.now();
    while (Date.now() - started < deadlineMs) {
      if (!isRouteCurrent(routeSeq)) return false;
      try {
        const out = await fetchSnapshotImage(imgPath, 12000);
        if (out.ok && out.blob) {
          showSnapshotBlob(preview, out.blob);
          return true;
        }
        if (out.status === 401 || out.status === 403) {
          if (msg) {
            msg.textContent = "Sign in required for snapshot preview";
            msg.className = "badge offline";
          }
          return false;
        }
        if (out.status === 410) {
          if (msg) {
            msg.textContent = "Image expired or removed";
            msg.className = "badge offline";
          }
          return false;
        }
        if (out.status === 503) {
          if (msg) {
            msg.textContent = "Server busy — try again shortly";
            msg.className = "badge offline";
          }
          return false;
        }
        if (out.status === 404) {
          if (msg) msg.textContent = "Waiting for board snapshot…";
        } else if (msg) {
          msg.textContent = "Waiting for board snapshot…";
        }
      } catch (_e) {
        if (msg) msg.textContent = "Waiting for board snapshot…";
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
    return false;
  };

  if (!canEdit) return;

  const searchBtn = view.querySelector("#devCamSearch");
  const searchStatus = view.querySelector("#devCamSearchStatus");
  const discoverList = view.querySelector("#devCamDiscoverList");
  const bindingsEl = view.querySelector("#devCamBindings");
  let tenantCameras = [];
  let discoverySince = "";

  const setSearchStatus = (txt, kind) => {
    if (!searchStatus) return;
    searchStatus.textContent = String(txt || "");
    searchStatus.className = kind === "ok" ? "badge online" : kind === "err" ? "badge offline" : "muted";
  };

  const renderDiscover = (devices) => {
    if (!discoverList) return;
    const rows = Array.isArray(devices) ? devices : [];
    if (!rows.length) {
      discoverList.innerHTML = "<p class=\"muted\">No cameras found on the network (board must be online with camera search support).</p>";
      return;
    }
    discoverList.innerHTML = `<div class="row" style="gap:6px;flex-wrap:wrap">${rows
      .map((d, i) => {
        const host = escapeHtml(String(d.host || ""));
        const brand = escapeHtml(String(d.brand || "ONVIF"));
        const label = escapeHtml(`${brand} · ${d.host || d.label || ""}`);
        return `<button type="button" class="btn sm secondary js-pick-discovered" data-idx="${i}" data-host="${host}" data-proto="${escapeHtml(String(d.protocol || "onvif"))}" title="${host}">${label}</button>`;
      })
      .join("")}</div>`;
    discoverList.querySelectorAll(".js-pick-discovered").forEach((btn) => {
      btn.addEventListener("click", () => {
        const hostIn = view.querySelector("#devCamHost");
        const protoIn = view.querySelector("#devCamProto");
        const labelIn = view.querySelector("#devCamLabel");
        if (hostIn) hostIn.value = btn.getAttribute("data-host") || "";
        if (protoIn) protoIn.value = btn.getAttribute("data-proto") || "onvif";
        if (labelIn && !String(labelIn.value || "").trim()) labelIn.value = btn.textContent || "";
        toast("Host filled from discovery", "ok");
      });
    });
  };

  const loadBindings = async () => {
    if (!bindingsEl) return;
    bindingsEl.textContent = "Loading…";
    try {
      const pack = await api(camPath(buildGetAdminDevicesCameraBindingsPath(deviceId)), {
        timeoutMs,
      });
      if (!isRouteCurrent(routeSeq)) return;
      const items = (pack && pack.items) || [];
      if (!items.length) {
        bindingsEl.innerHTML = "<p class=\"muted\">No camera bound to this board yet.</p>";
        return;
      }
      bindingsEl.innerHTML = `<table class="data-table"><thead><tr><th>Camera</th><th>Cooldown</th><th>Status</th><th></th></tr></thead><tbody>${items
        .map((b) => {
          const cam = tenantCameras.find((c) => c.camera_id === b.camera_id);
          const label = cam ? cam.label || cam.host : b.camera_id;
          const st = cam ? cam.credential_status || cam.health_status || "—" : "—";
          return `<tr>
            <td>${escapeHtml(label || b.camera_id || "")}</td>
            <td>${escapeHtml(String(b.cooldown_s || ""))}s</td>
            <td>${escapeHtml(st)}</td>
            <td><button class="btn sm danger js-dev-cam-unbind" data-bid="${escapeHtml(b.binding_id || "")}">Unbind</button></td>
          </tr>`;
        })
        .join("")}</tbody></table>`;
      bindingsEl.querySelectorAll(".js-dev-cam-unbind").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const bid = btn.getAttribute("data-bid");
          if (!bid || !confirm("Remove binding?")) return;
          try {
            await api(camPath(buildDeleteAdminDevicesCameraBindingsPath(deviceId, bid)), {
              method: "DELETE",
              timeoutMs,
            });
            toast("Unbound", "ok");
            await loadBindings();
          } catch (e) {
            toast((e && e.message) || "Unbind failed", "err");
          }
        });
      });
    } catch (_e) {
      if (!isRouteCurrent(routeSeq)) return;
      bindingsEl.innerHTML = "<p class=\"muted\">Cameras are not available for this account yet.</p>";
    }
  };

  const loadTenantCameras = async () => {
    try {
      const pack = await api(camPath(buildGetAdminCamerasPath()), { timeoutMs });
      if (!isRouteCurrent(routeSeq)) return;
      tenantCameras = (pack && pack.items) || [];
      await loadBindings();
    } catch (_e) {
      if (!isRouteCurrent(routeSeq)) return;
      tenantCameras = [];
      if (bindingsEl) bindingsEl.innerHTML = "<p class=\"muted\">Could not load cameras.</p>";
    }
  };

  const pollDiscovery = async () => {
    const deadline = Date.now() + 55000;
    while (Date.now() < deadline) {
      if (!isRouteCurrent(routeSeq)) return;
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const pack = await api(
          camPath(buildGetAdminDevicesCameraDiscoveryPath(deviceId, { since: discoverySince })),
          { timeoutMs }
        );
        const st = String((pack && pack.status) || "pending").toLowerCase();
        if (st === "scanning" || st === "pending") {
          setSearchStatus("Scanning LAN…", "");
          continue;
        }
        if (st === "error" || st === "failed") {
          setSearchStatus((pack && pack.error) || "Discovery failed", "err");
          renderDiscover([]);
          return;
        }
        if (st === "complete" || st === "done" || st === "ok" || (pack && pack.devices && pack.devices.length)) {
          setSearchStatus(`Found ${(pack.devices || []).length} camera(s)`, "ok");
          renderDiscover(pack.devices || []);
          return;
        }
      } catch (e) {
        setSearchStatus((e && e.message) || "Poll failed", "err");
        return;
      }
    }
    setSearchStatus("Discovery timed out (45s)", "err");
    renderDiscover([]);
  };

  if (searchBtn) {
    searchBtn.addEventListener("click", async () => {
      discoverySince = new Date().toISOString();
      searchBtn.disabled = true;
      setSearchStatus("Starting camera search…", "");
      if (discoverList) discoverList.innerHTML = "<p class=\"muted\">Waiting for board scan…</p>";
      try {
        await api(camPath(buildPostAdminDevicesCameraDiscoveryPath(deviceId)), { method: "POST", timeoutMs });
        await pollDiscovery();
      } catch (e) {
        setSearchStatus((e && e.message) || "Discovery start failed", "err");
        toast((e && e.message) || "Discovery failed", "err");
      } finally {
        searchBtn.disabled = false;
      }
    });
  }

  const addForm = view.querySelector("#devCamAddForm");
  if (addForm) {
    addForm.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const host = String((view.querySelector("#devCamHost") || {}).value || "").trim();
      if (!host) {
        toast("Host required", "err");
        return;
      }
      try {
        const created = await api(camPath(buildPostAdminCamerasPath()), {
          method: "POST",
          timeoutMs,
          body: {
            label: String((view.querySelector("#devCamLabel") || {}).value || "").trim(),
            host,
            protocol: String((view.querySelector("#devCamProto") || {}).value || "onvif"),
            username: String((view.querySelector("#devCamUser") || {}).value || ""),
            password: String((view.querySelector("#devCamPass") || {}).value || ""),
          },
        });
        const camId = created && (created.camera_id || created.id);
        if (camId) {
          await api(camPath(buildPostAdminDevicesCameraBindingsPath(deviceId)), {
            method: "POST",
            timeoutMs,
            body: { camera_id: camId, cooldown_s: 20 },
          });
        }
        toast("Camera saved and bound", "ok");
        const passEl = view.querySelector("#devCamPass");
        if (passEl) passEl.value = "";
        await loadTenantCameras();
      } catch (e) {
        toast((e && e.message) || "Save failed", "err");
      }
    });
  }

  const testBtn = view.querySelector("#devCamTestNew");
  if (testBtn) {
    testBtn.addEventListener("click", async () => {
      const host = String((view.querySelector("#devCamHost") || {}).value || "").trim();
      if (!host) {
        toast("Host required", "err");
        return;
      }
      const msg = view.querySelector("#devCamTestMsg");
      const preview = view.querySelector("#devCamTestPreview");
      if (msg) msg.textContent = "Testing via edge…";
      if (preview) preview.innerHTML = "";
      try {
        const pack = await api(camPath(buildPostAdminCamerasTestCapturePath()), {
          method: "POST",
          timeoutMs: CAMERAS_SNAPSHOT_IMAGE_TIMEOUT_MS,
          body: {
            device_id: deviceId,
            host,
            protocol: String((view.querySelector("#devCamProto") || {}).value || "hikvision"),
            username: String((view.querySelector("#devCamUser") || {}).value || ""),
            password: String((view.querySelector("#devCamPass") || {}).value || ""),
            send_email: false,
          },
        });
        const ok = !!(pack && pack.ok);
        const tag = (pack && pack.tag) || (pack && pack.error) || "unknown";
        if (!ok) {
          if (msg) {
            msg.textContent = `Failed (${tag})`;
            msg.className = "badge offline";
          }
          toast(`Failed (${tag})`, "err");
          return;
        }
        if (msg) {
          msg.textContent = pack.poll ? "Queued — waiting for board…" : "OK (edge)";
          msg.className = "badge online";
        }
        if (pack.snapshot_id && pack.poll) {
          const imgPath = camPath(buildGetAdminCameraSnapshotImagePath(String(pack.snapshot_id)));
          const loaded = await pollSnapshotPreview(imgPath, preview, msg, 45000);
          if (!isRouteCurrent(routeSeq)) return;
          if (loaded) {
            if (msg) msg.textContent = "OK (edge)";
            toast("Snapshot OK", "ok");
          } else {
            if (msg) {
              msg.textContent = "Timeout (edge)";
              msg.className = "badge offline";
            }
            toast("Snapshot timed out — check camera and board link", "err");
          }
          return;
        }
        if (preview && pack.snapshot_id) {
          const imgPath = camPath(buildGetAdminCameraSnapshotImagePath(String(pack.snapshot_id)));
          const out = await fetchSnapshotImage(imgPath, timeoutMs);
          if (out.ok && out.blob) showSnapshotBlob(preview, out.blob);
        }
        toast("Snapshot OK", "ok");
      } catch (e) {
        if (msg) msg.textContent = (e && e.message) || "Test failed";
        toast((e && e.message) || "Test failed", "err");
      }
    });
  }

  void loadTenantCameras();
}
